import { Lock, Quote, ShieldCheck, UserCheck, Trash2, AlertTriangle } from "lucide-react";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import MarketingCta from "../components/MarketingCta";
import Footer from "../components/Footer";

const SECTIONS = [
  {
    icon: Lock,
    title: "Data Handling",
    body: [
      "Documents are encrypted in transit (TLS) and at rest. Uploaded agreements and supporting documents are stored in CBAIQ's managed cloud infrastructure and associated only with your workspace.",
      "Your documents are processed solely to answer your questions. They are not shared with other customers, and they are not visible to anyone outside your workspace.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Our Commitments, Stated Plainly",
    body: [
      "Your agreements are never used to train AI models — neither ours nor any third party's.",
      "Your documents are never externally indexed. They cannot appear in search engines or any external system.",
      "Your data remains under your organization's control.",
    ],
  },
  {
    icon: UserCheck,
    title: "Access Control",
    body: [
      "Access is restricted to the named users (seats) in your workspace. Each user authenticates with their own credentials, and answer history is tied to the workspace so administrators can see how the tool is being used.",
      "Organization plans add SSO and audit exports for teams with stricter governance requirements.",
    ],
  },
  {
    icon: Quote,
    title: "How Citations Work",
    body: [
      "When you upload an agreement, CBAIQ breaks it into its articles and clauses and builds an index specific to that document. When you ask a question, the system retrieves the most relevant passages from your agreement and generates an answer grounded in those passages — citing the specific article language it relied on.",
      "This means every answer is traceable: you can open the cited article and verify the language yourself. The citation is the product — not a decoration.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "Accuracy: Our Approach and Its Limits",
    body: [
      "Grounding answers in your agreement's actual text and citing the source dramatically reduces — but does not eliminate — the risk of AI error. Language can be ambiguous, agreements can conflict with statute or past practice, and some questions genuinely require professional judgment.",
      "CBAIQ is designed to be honest about its limits: when a question goes beyond what your documents can answer, or carries significant legal risk, the tool says so and recommends consulting your labour relations advisors or legal counsel rather than guessing.",
      "Always verify cited language before acting, and treat CBAIQ's output as decision support — not a decision.",
    ],
  },
  {
    icon: Trash2,
    title: "Deletion on Request",
    body: [
      "You can delete documents from your workspace at any time. On request, we will delete your organization's documents and associated data from our systems. Contact support@cbaiq.com.",
    ],
  },
];

const SECURITY_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Security & Accuracy | CBAIQ",
  "url": "https://cbaiq.com/security",
  "description": "How CBAIQ protects collective agreements: encryption, no model training on your data, no external indexing, access control, citation traceability, and honest accuracy limits.",
  "about": { "@type": "Organization", "@id": "https://cbaiq.com/#organization" },
};

export default function Security() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="security-page">
      <Seo
        title="Security & Accuracy | CBAIQ"
        description="How CBAIQ protects your collective agreements: encryption in transit and at rest, never used for model training, never externally indexed, access controls, traceable citations, and honest accuracy limits."
        path="/security"
        jsonLd={SECURITY_JSONLD}
      />
      <PublicHeader />

      <div className="flex-1">
        <section className="pt-20 sm:pt-24 pb-12 px-4" data-testid="security-hero">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-4">Security &amp; Accuracy</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight tracking-tight text-foreground mb-5">
              Built for Documents You Can't Afford to Mishandle
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              Collective agreements, settlements, and case details are sensitive labour relations records.
              Here is exactly how CBAIQ handles them — and exactly what the tool will and won't claim.
            </p>
          </div>
        </section>

        <section className="pb-16 px-4" data-testid="security-sections">
          <div className="max-w-3xl mx-auto space-y-10">
            {SECTIONS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="border-t border-border/40 pt-8" data-testid={`security-section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">{title}</h2>
                </div>
                <div className="space-y-3">
                  {body.map((p, i) => (
                    <p key={i} className="text-sm text-muted-foreground leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            ))}
            <div className="border-t border-border/40 pt-8">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Legal disclaimer:</span> CBAIQ is an educational
                decision-support tool, not a law firm, and does not provide legal advice.
              </p>
            </div>
          </div>
        </section>

        <MarketingCta
          page="security"
          heading="See How It Handles Your Agreement."
          subtext="Guided 14-day pilot — upload one agreement, get 20 answered questions, onboarding call included. No credit card."
        />
      </div>
      <Footer />
    </div>
  );
}
